// El comprobante del cobro en efectivo a pantalla completa (2026-11-01): para mostrárselo al cliente y
// compartírselo (WhatsApp, correo…) como imagen. Lo dibuja el celular con los datos que guardó el ERP.
import { useEffect, useState } from 'react';
import { guardarArchivo } from '../api/cliente';
import {
  archivoComprobante,
  dibujarComprobante,
  type VistaRecepcion,
} from '../tesoreria/comprobanteRuta';

const ESTADO: Record<VistaRecepcion['estado'], { texto: string; tono: string }> = {
  PENDIENTE: { texto: 'Por ingresar a tesorería', tono: 'ambar' },
  INGRESADA: { texto: 'Ingresado a tesorería', tono: 'verde' },
  ANULADA: { texto: 'Anulado', tono: 'rojo' },
};

export function VisorComprobante({ v, alCerrar }: { v: VistaRecepcion; alCerrar: () => void }) {
  const [png, setPng] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    let u: string | null = null;
    dibujarComprobante(v)
      .then((b) => {
        if (!vivo) return;
        setPng(b);
        u = URL.createObjectURL(b);
        setUrl(u);
      })
      .catch((e: unknown) => vivo && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      vivo = false;
      if (u) URL.revokeObjectURL(u);
    };
  }, [v]);

  // Mientras está abierto, la página de atrás no se mueve; Escape cierra (en el computador).
  useEffect(() => {
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const tecla = (e: KeyboardEvent) => e.key === 'Escape' && alCerrar();
    window.addEventListener('keydown', tecla);
    return () => {
      document.body.style.overflow = antes;
      window.removeEventListener('keydown', tecla);
    };
  }, [alCerrar]);

  const archivo = png ? new File([png], archivoComprobante(v), { type: 'image/png' }) : null;
  const puedeCompartir =
    !!archivo &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [archivo] });
  const compartir = async () => {
    if (!archivo) return;
    setAviso(null);
    try {
      await navigator.share({ files: [archivo], title: `Comprobante ${v.numero}` });
    } catch (e) {
      // Cerrar el menú de compartir no es un error.
      if ((e as Error).name !== 'AbortError')
        setAviso(`No se pudo compartir: ${(e as Error).message}`);
    }
  };
  const estado = ESTADO[v.estado];

  return (
    <div className="visor" role="dialog" aria-modal="true" aria-label={`Comprobante ${v.numero}`}>
      <div className="visor-barra">
        <button className="btn sec sm" type="button" onClick={alCerrar}>
          ✕ Cerrar
        </button>
        <div className="t">{v.numero}</div>
        <span className={`chip ${estado.tono}`}>
          {estado.texto}
          {v.resolucion?.recibo ? ` · ${v.resolucion.recibo.numero}` : ''}
        </span>
      </div>
      <div className="visor-cuerpo">
        {error ? (
          <div className="error" style={{ margin: 16 }}>
            {error}
          </div>
        ) : url ? (
          <img className="visor-img" src={url} alt={`Comprobante ${v.numero}`} />
        ) : (
          <div className="vacio">Armando el comprobante…</div>
        )}
      </div>
      {png && (
        <div className="visor-acc">
          {aviso && <div className="error">{aviso}</div>}
          <div className="botones">
            {puedeCompartir && (
              <button className="btn verde" type="button" onClick={() => void compartir()}>
                Compartir con el cliente
              </button>
            )}
            <button
              className="btn sec"
              type="button"
              onClick={() => guardarArchivo(png, archivoComprobante(v))}
            >
              Descargar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
